import { type ComponentProps, type ReactElement } from 'react';
import { cn } from '@avanta/shared/utils/cn';

function Table({ className, ...props }: ComponentProps<'table'>): ReactElement {
  return (
    <div data-slot="table-container" className="notranslate bg-white" translate="no">
      <table
        data-slot="table"
        className={cn(
          'w-full caption-bottom text-sm border-separate border-spacing-y-2',
          className
        )}
        {...props}
        translate="no"
      />
    </div>
  );
}

function TableHeader({ className, ...props }: ComponentProps<'thead'>): ReactElement {
  return <thead data-slot="table-header" className={cn(className)} {...props} />;
}

function TableBody({ className, ...props }: ComponentProps<'tbody'>): ReactElement {
  return <tbody data-slot="table-body" className={cn(className)} {...props} />;
}

function TableFooter({ className, ...props }: ComponentProps<'tfoot'>): ReactElement {
  return <tfoot data-slot="table-footer" className={cn('font-medium', className)} {...props} />;
}

function TableRow({ className, ...props }: ComponentProps<'tr'>): ReactElement {
  return <tr data-slot="table-row" className={cn('group', className)} {...props} />;
}

function TableHead({ className, ...props }: ComponentProps<'th'>): ReactElement {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "text-denim-800 h-6 px-4 text-left align-middle font-medium! leading-4 whitespace-nowrap text-xs font-['Poppins'] has-[[role=checkbox]]:pr-0 *:[[role=checkbox]]:translate-y-0.5",
        className
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: ComponentProps<'td'>): ReactElement {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        'text-denim-900 h-12 px-4 align-middle whitespace-nowrap border-plum-200 border-t border-b first:rounded-l-xl first:border-l last:rounded-r-xl last:border-r group-hover:bg-plum-100 group-data-[state=selected]:bg-plum-100 transition-colors text-sm has-[[role=checkbox]]:pr-0 *:[[role=checkbox]]:translate-y-0.5',
        className
      )}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: ComponentProps<'caption'>): ReactElement {
  return (
    <caption
      data-slot="table-caption"
      className={cn('text-gray-600 mt-4 text-sm', className)}
      {...props}
    />
  );
}

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
